import { useCallback, useEffect, useRef } from 'react';

const MOBILE_FIELD_SELECTOR = '[data-mobile-sequence="true"]';
const FOCUS_RETRY_FRAMES = 12;

const isVisibleEditableField = (element) => {
  if (!element || element.disabled || element.readOnly) return false;
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
};

const findScrollableParent = (element) => {
  let parent = element?.parentElement;
  while (parent && parent !== document.body) {
    const { overflowY } = window.getComputedStyle(parent);
    if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

export const keepMobileFieldVisible = (element, behavior = 'smooth') => {
  if (!element || window.matchMedia('(min-width: 768px)').matches) return;

  const viewport = window.visualViewport;
  const viewportTop = viewport?.offsetTop || 0;
  const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
  const scrollParent = findScrollableParent(element);
  const parentRect = scrollParent === document.scrollingElement || scrollParent === document.documentElement
    ? { top: viewportTop, bottom: viewportBottom }
    : scrollParent.getBoundingClientRect();
  const stickyActions = document.querySelector('[data-mobile-sticky-actions="true"]');
  const stickyRect = stickyActions?.getBoundingClientRect();

  const safeTop = Math.max(viewportTop + 12, parentRect.top + 12);
  let safeBottom = Math.min(viewportBottom - 16, parentRect.bottom - 16);
  if (stickyRect && stickyRect.top < safeBottom && stickyRect.bottom > safeTop) {
    safeBottom = stickyRect.top - 12;
  }

  const fieldRect = element.getBoundingClientRect();
  let scrollDelta = 0;
  if (fieldRect.bottom > safeBottom) {
    scrollDelta = fieldRect.bottom - safeBottom;
  } else if (fieldRect.top < safeTop) {
    scrollDelta = fieldRect.top - safeTop;
  }

  if (Math.abs(scrollDelta) < 1) return;
  scrollParent.scrollBy({ top: scrollDelta, left: 0, behavior });
};

export const focusMobileFieldByTestId = (testId, options = {}) => {
  const { behavior = 'smooth', attempts = FOCUS_RETRY_FRAMES } = options;
  let attempt = 0;

  const focusWhenReady = () => {
    const element = document.querySelector(`[data-testid="${testId}"]`);
    if (isVisibleEditableField(element)) {
      element.focus({ preventScroll: true });
      keepMobileFieldVisible(element, behavior);
      window.setTimeout(() => {
        if (document.activeElement === element) keepMobileFieldVisible(element, behavior);
      }, 180);
      return;
    }
    attempt += 1;
    if (attempt < attempts) window.requestAnimationFrame(focusWhenReady);
  };

  window.requestAnimationFrame(focusWhenReady);
};

export const useMobileSequentialNavigation = (enabled) => {
  const formRef = useRef(null);
  const activeFieldRef = useRef(null);

  const getFields = useCallback(() => {
    if (!formRef.current) return [];
    return Array.from(formRef.current.querySelectorAll(MOBILE_FIELD_SELECTOR)).filter(isVisibleEditableField);
  }, []);

  const refreshEnterKeyHints = useCallback(() => {
    if (!enabled) return;
    const fields = getFields();
    fields.forEach((field, index) => {
      const continuesWithNewRow = Boolean(field.dataset.mobileAppendButtonTestid);
      field.enterKeyHint = index === fields.length - 1 && !continuesWithNewRow ? 'done' : 'next';
    });
  }, [enabled, getFields]);

  const focusField = useCallback((field) => {
    if (!field) return;
    activeFieldRef.current = field;
    field.focus({ preventScroll: true });
    keepMobileFieldVisible(field);
    window.setTimeout(() => {
      if (document.activeElement === field) keepMobileFieldVisible(field);
    }, 180);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (!enabled || event.key !== 'Enter' || event.isComposing) return;
    if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;

    const currentField = event.target.closest?.(MOBILE_FIELD_SELECTOR);
    if (!currentField) return;

    event.preventDefault();
    const nextTestId = currentField.dataset.mobileNextTestid;
    const appendButtonTestId = currentField.dataset.mobileAppendButtonTestid;
    const appendBeforeFollowingFields = currentField.dataset.mobileAppendPriority === 'true';

    const appendAndFocus = () => {
      if (
        !appendButtonTestId
        || !nextTestId
        || currentField.value.trim() === ''
        || document.querySelector(`[data-testid="${nextTestId}"]`)
      ) return false;

      const appendButton = document.querySelector(`[data-testid="${appendButtonTestId}"]`);
      if (!appendButton) return false;
      appendButton.click();
      focusMobileFieldByTestId(nextTestId);
      return true;
    };

    if (appendBeforeFollowingFields && appendAndFocus()) return;

    const fields = getFields();
    const currentIndex = fields.indexOf(currentField);
    const nextField = fields[currentIndex + 1];
    if (nextField) {
      focusField(nextField);
      return;
    }
    appendAndFocus();
  }, [enabled, focusField, getFields]);

  useEffect(() => {
    if (!enabled || !formRef.current) return undefined;
    refreshEnterKeyHints();

    const observer = new MutationObserver(refreshEnterKeyHints);
    observer.observe(formRef.current, { childList: true, subtree: true });

    const handleFocusIn = (event) => {
      if (!event.target.matches?.(MOBILE_FIELD_SELECTOR)) return;
      activeFieldRef.current = event.target;
      window.requestAnimationFrame(() => keepMobileFieldVisible(event.target));
    };
    const handleViewportResize = () => {
      if (activeFieldRef.current === document.activeElement) {
        keepMobileFieldVisible(activeFieldRef.current);
      }
    };

    const form = formRef.current;
    form.addEventListener('focusin', handleFocusIn);
    window.visualViewport?.addEventListener('resize', handleViewportResize);
    return () => {
      observer.disconnect();
      form.removeEventListener('focusin', handleFocusIn);
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
    };
  }, [enabled, refreshEnterKeyHints]);

  return { formRef, handleKeyDown };
};